import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Share2, Check, Users } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { shareEvent } from "@/lib/shareEvent";

export const rsvpLoginPath = (eventId) =>
  `/login?next=${encodeURIComponent(`/live/events/${eventId}?rsvp=1`)}`;

// Reserve My Spot / Cancel + Share Event for RGM Studio events.
// RSVP is never an access gate — Join Live is rendered separately.
export const EventRsvpControls = ({ ev, currentUser, rsvpOpen, onUpdated, onViewAttendees }) => {
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const id = ev.event_id;
  const count = ev.rsvp_count || 0;
  const isHost = currentUser && currentUser.user_id === ev.host_user_id;
  const canManage = isHost || ["admin", "moderator"].includes(currentUser?.role);

  const toggle = async () => {
    if (!currentUser) {
      toast("Log in or sign up to reserve your spot");
      navigate(rsvpLoginPath(id));
      return;
    }
    if (busy) return;
    setBusy(true);
    try {
      const r = ev.viewer_rsvped
        ? await api.delete(`/live/events/${id}/rsvp`)
        : await api.post(`/live/events/${id}/rsvp`);
      onUpdated?.(id, { viewer_rsvped: r.data.rsvped, rsvp_count: r.data.rsvp_count });
      toast.success(r.data.rsvped ? "Your spot is reserved!" : "Reservation cancelled");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update your reservation");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-2" data-testid={`rsvp-controls-${id}`}>
      <div className="flex items-center justify-between text-xs">
        <span className="text-gray-300" data-testid={`rsvp-count-${id}`}>
          <span className="text-white font-bold">{count}</span> {count === 1 ? "spot reserved" : "spots reserved"}
        </span>
        {ev.viewer_rsvped && (
          <span className="inline-flex items-center gap-1 text-emerald-300 font-bold" data-testid={`rsvp-confirmed-${id}`}>
            <Check className="w-3.5 h-3.5" /> You're registered
          </span>
        )}
      </div>
      <div className="flex gap-2">
        {rsvpOpen && !isHost && (
          ev.viewer_rsvped ? (
            <Button variant="outline" disabled={busy} onClick={toggle}
              className="flex-1 text-gray-200 border-gray-600 hover:bg-gray-800"
              data-testid={`rsvp-cancel-${id}`}>
              {busy ? "Cancelling…" : "Cancel Reservation"}
            </Button>
          ) : (
            <Button disabled={busy} onClick={toggle}
              className="flex-1 font-bold bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white"
              data-testid={`rsvp-reserve-${id}`}>
              {busy ? "Reserving…" : "Reserve My Spot"}
            </Button>
          )
        )}
        {canManage && onViewAttendees && (
          <Button variant="outline" onClick={onViewAttendees}
            className="flex-1 text-purple-100 border-purple-500/50 hover:bg-purple-500/10"
            data-testid={`rsvp-attendees-${id}`}>
            <Users className="w-4 h-4 mr-1" /> Attendees ({count})
          </Button>
        )}
        <Button variant="outline" onClick={() => shareEvent(ev)}
          className="text-white border-gray-700 hover:bg-gray-800"
          data-testid={`share-event-${id}`}>
          <Share2 className="w-4 h-4 mr-1" /> Share Event
        </Button>
      </div>
    </div>
  );
};

export default EventRsvpControls;
